import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { timesheetService } from '../services/timesheetService';
import type { SalvarTimesheetRequest, SalvarCustoHoraRequest } from '../types';
import type { FiltrosPendentes } from '../services/timesheetService';

const KEYS = {
  porFuncionario: (f: number, i: string, fim: string) => ['timesheet', 'func', f, i, fim] as const,
  pendentes: (p: number, f?: FiltrosPendentes) => ['timesheet', 'pendentes', p, f ?? null] as const,
  custoHora: ['timesheet', 'custo-hora'] as const,
};

export function useTimesheetFuncionario(funcionarioId: number, inicio: string, fim: string) {
  return useQuery({
    queryKey: KEYS.porFuncionario(funcionarioId, inicio, fim),
    queryFn: () => timesheetService.listarPorFuncionario(funcionarioId, inicio, fim),
    enabled: funcionarioId > 0 && !!inicio && !!fim,
  });
}

export function useTimesheetPendentes(projetoId = 0, filtros?: FiltrosPendentes) {
  return useQuery({
    queryKey: KEYS.pendentes(projetoId, filtros),
    queryFn: () => timesheetService.pendentes(projetoId, filtros),
  });
}

export function useCustosHora() {
  return useQuery({ queryKey: KEYS.custoHora, queryFn: () => timesheetService.listarCustosHora() });
}

export function useTimesheetMutations() {
  const qc = useQueryClient();
  const invalAll = () => {
    qc.invalidateQueries({ queryKey: ['timesheet'] });
    qc.invalidateQueries({ queryKey: ['projetos'] });
    qc.invalidateQueries({ queryKey: ['projeto'] });
  };

  const criar = useMutation({
    mutationFn: (req: SalvarTimesheetRequest) => timesheetService.criar(req),
    onSuccess: invalAll,
  });
  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarTimesheetRequest }) => timesheetService.atualizar(id, req),
    onSuccess: invalAll,
  });
  const excluir = useMutation({
    mutationFn: (id: number) => timesheetService.excluir(id),
    onSuccess: invalAll,
  });
  const aprovar = useMutation({
    mutationFn: (ids: number[]) => timesheetService.aprovar(ids),
    onSuccess: invalAll,
  });
  const rejeitar = useMutation({
    mutationFn: ({ ids, motivo }: { ids: number[]; motivo?: string }) => timesheetService.rejeitar(ids, motivo),
    onSuccess: invalAll,
  });
  const salvarCustoHora = useMutation({
    mutationFn: (req: SalvarCustoHoraRequest) => timesheetService.salvarCustoHora(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.custoHora }),
  });

  return { criar, atualizar, excluir, aprovar, rejeitar, salvarCustoHora };
}
